import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Wrench, Trophy, Zap, Star, Video, CalendarDays, Award, Building2, ShieldCheck } from "lucide-react";

const DIR_META = {
  office_to_maintenance: { title: "Office Staff → Maintenance", icon: Wrench },
  maintenance_to_office: { title: "Maintenance Staff → Office", icon: Trophy },
  cross_division_bonus: { title: "Cross-Division Bonus (optional, +10 each — same two for every property)", icon: Zap },
  support_team: { title: "Support Team tasks (for properties — remote counts)", icon: Building2 },
};

const TIMELINE = [
  ["Jul 9", "Competition begins — start logging tasks"],
  ["Fridays", "Leaderboard updates + Weekly Shoutouts"],
  ["Jul 31", "Tasks + reels due — and the Lazy Ironman deadline"],
  ["After Jul 31", "Judging and winners follow the July 31 deadline — watch the leaderboard and Teams for announcements"],
];

export default function HowItWorks() {
  const [tasks, setTasks] = useState([]);
  const [prizes, setPrizes] = useState([]);

  useEffect(() => {
    api.get("/tasks").then(({ data }) => setTasks(data));
    api.get("/prizes").then(({ data }) => setPrizes(data));
  }, []);

  const groups = ["office_to_maintenance", "maintenance_to_office", "cross_division_bonus", "support_team"]
    .map((d) => ({ dir: d, items: tasks.filter((t) => t.direction === d) }));

  return (
    <div>
      <section className="sw-navy text-white">
        <div className="max-w-3xl mx-auto px-4 py-12">
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#ff4d64] mb-3">The rules, in plain words</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter">How Site Wars works</h1>
          <p className="mt-4 text-slate-300">All July, office and maintenance <span className="text-white font-semibold">swap roles</span>. Office staff earn points doing real maintenance tasks; maintenance techs earn points doing office work. Every property films one short reel. Properties compete in <span className="text-white font-semibold">six brackets</span> (Student or Conventional × Small, Medium, Large) — one Golden Plunger per bracket. The home-office <span className="text-white font-semibold">Support Corps competes too</span>, floor against floor, on property visits. Every property is already entered; just claim yours and start logging.</p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 py-10 space-y-12">
        <section data-testid="hiw-scoring">
          <h2 className="font-display text-2xl font-semibold text-[#1a1f36] mb-3 flex items-center gap-2"><Star className="w-5 h-5 text-[#c8102e]" />Scoring, in plain words</h2>
          <ol className="space-y-2 text-slate-700 list-decimal ml-5">
            <li><b>Every approved task earns points</b> (10 / 25 / 50 — full list below). Each task counts once per person and needs a supervisor sign-off + a photo.</li>
            <li>Your total task points are <b>divided by your capacity</b> (beds for Student, units for Conventional) — so a 60-bed property competes fairly against a 600-bed one.</li>
            <li>Within your bracket, the top per-capacity score is set to <b>100</b>, everyone else scales to it. Your reel (judged out of 30) is indexed the same way.</li>
            <li><b>Final Score = task index × 70% + reel index × 30%.</b> Highest in each bracket wins.</li>
            <li><b>Commander Bonus:</b> get your Regional to complete one task with your team (photo, admin-verified) for <b>+15 raw points</b>.</li>
            <li><b>Host a Support Corps visit:</b> every approved visit from a home-office floor member automatically adds <b>+10 raw points</b> to your property — up to 3 visits (30 points). No extra logging needed on your side.</li>
            <li><b>Lazy Ironman Finisher:</b> any team — property or Support floor — that completes the Lazy Ironman challenge by <b>July 31</b> earns a one-time <b>+25 raw points</b> (admin-verified).</li>
          </ol>
          <div className="mt-4 border border-slate-200 rounded-md p-4 bg-[#f8f9fc] text-sm text-slate-700">
            <div className="font-semibold text-[#1a1f36] mb-1">Worked example</div>
            Maple Court (120 beds) has 90 approved points → 0.75 per bed. Rival Oak Hall (300 beds) has 180 points → 0.60 per bed. Maple leads: its task index is 100, Oak's is 80. If Oak's reel scores 27/30 (best in bracket = index 100) and Maple's scores 21 (index 77.8): Maple = 100×0.7 + 77.8×0.3 = <b>93.3</b>; Oak = 80×0.7 + 100×0.3 = <b>86.0</b>. Maple wins the bracket — small property, big hustle.
          </div>
        </section>

        <section data-testid="hiw-tasks">
          <h2 className="font-display text-2xl font-semibold text-[#1a1f36] mb-3 flex items-center gap-2"><Wrench className="w-5 h-5 text-[#c8102e]" />The full task list</h2>
          <p className="text-sm text-slate-600 mb-4">Once per person, supervisor sign-off + one photo, every time. Every property sees the same two bonus tasks. Support Team tasks are for properties — do them with a home-office department, in person or over Teams. Support Corps floor members run these exact same task lists on their property visits.</p>
          <Accordion type="multiple" className="space-y-2">
            {groups.map(({ dir, items }) => items.length > 0 && (
              <AccordionItem key={dir} value={dir} className="border border-slate-200 rounded-md px-4">
                <AccordionTrigger className="font-display font-semibold text-[#1a1f36] hover:no-underline py-3.5" data-testid={`hiw-tasks-toggle-${dir}`}>
                  <span className="flex items-center justify-between w-full pr-2 gap-2 text-left">
                    {DIR_META[dir].title}
                    <span className="text-xs font-sans font-normal text-slate-400 shrink-0">{items.length} tasks</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-2">
                  <div className="divide-y divide-slate-100">
                    {items.map((t) => (
                      <div key={t.id} className="py-2.5 flex items-center justify-between gap-3 text-sm">
                        <div>
                          <span className="text-slate-800">{t.name}</span>
                          <div className="text-xs text-slate-500">Sign-off: {t.supervised_by}</div>
                        </div>
                        <span className="font-display font-bold text-[#c8102e] shrink-0">{t.points}</span>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        <section data-testid="hiw-support">
          <h2 className="font-display text-2xl font-semibold text-[#1a1f36] mb-3 flex items-center gap-2"><Building2 className="w-5 h-5 text-[#c8102e]" />Home office plays too — the Support Corps</h2>
          <ul className="space-y-2 text-slate-700 text-sm list-disc ml-5">
            <li>The home office competes as <b>three floor teams</b> — Floor 1, 2, and 3 — with their own leaderboard section. Pick your floor when you sign up (no capacity or Regional needed).</li>
            <li><b>Support folks do real onsite tasks with the teams — that's the whole point.</b> Floor members run the exact same task lists as properties (Office → Maintenance and Maintenance → Office), onsite or by Teams call.</li>
            <li>The floor member logs it in the app with the property picker: photo (or call screenshot) + the property supervisor's sign-off. One submission, two credits — on approval it's <b>+15 to the floor</b> and <b>+10 bonus points to the property</b>, automatically (max 3 visits per property). The property never logs a separate entry.</li>
            <li>Each task counts <b>once per person</b> — same rule as everyone else.</li>
            <li>No tiers, no reel, no 70/30 for floors — <b>most total visit points wins</b>. The winning floor gets featured leadership recognition and a catered team lunch.</li>
          </ul>
        </section>

        <section data-testid="hiw-safety">
          <h2 className="font-display text-2xl font-semibold text-[#1a1f36] mb-3 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#c8102e]" />Safety first — always</h2>
          <div className="border-l-4 border-[#c8102e] bg-[#f8f9fc] rounded-md p-4 text-sm text-slate-700">
            Approved tasks only; no electrical, no roofs, no ladders above step height, no chemicals beyond standard cleaning supplies; maintenance supervises every office-to-maintenance task; all tasks on the clock with normal PPE; anything that feels unsafe gets skipped.
          </div>
        </section>

        <section data-testid="hiw-timeline">
          <h2 className="font-display text-2xl font-semibold text-[#1a1f36] mb-3 flex items-center gap-2"><CalendarDays className="w-5 h-5 text-[#c8102e]" />Timeline</h2>
          <div className="border-l-2 border-[#c8102e] ml-2 space-y-4 pl-5">
            {TIMELINE.map(([when, what]) => (
              <div key={when}>
                <div className="text-xs font-bold uppercase tracking-wide text-[#c8102e]">{when}</div>
                <div className="text-slate-700 text-sm">{what}</div>
              </div>
            ))}
          </div>
        </section>

        <section data-testid="hiw-reel">
          <h2 className="font-display text-2xl font-semibold text-[#1a1f36] mb-3 flex items-center gap-2"><Video className="w-5 h-5 text-[#c8102e]" />The reel (30% of your score)</h2>
          <p className="text-slate-700 text-sm">One short video per property — post it anywhere (YouTube, Drive, Instagram, TikTok) and paste the link in your dashboard by Jul 31. Three judges each score three things out of 10: <b>Teamwork on screen</b>, <b>Creativity</b>, and <b>Teaches something real</b> — averaged, max 30. People's Choice voting is separate bragging rights and never touches the score. (Support floors skip the reel — their race is pure visit points.)</p>
        </section>

        <section data-testid="hiw-prizes">
          <h2 className="font-display text-2xl font-semibold text-[#1a1f36] mb-4 flex items-center gap-2"><Award className="w-5 h-5 text-[#c8102e]" />What's on the line</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {prizes.map((p) => (
              <div key={p.name} className="border border-slate-200 rounded-md p-4 hover:shadow-md transition-shadow">
                <div className="font-semibold text-[#1a1f36] text-sm">{p.name}</div>
                <div className="text-xs text-slate-500 mt-1">{p.detail}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="sw-navy rounded-md p-6 text-center">
          <p className="text-white font-display font-semibold text-lg mb-2">Your property is already in the fight.</p>
          <p className="text-slate-300 text-sm mb-4">Claim it to log tasks, save your reel, and watch your standing climb.</p>
          <a href="/register" className="inline-block sw-red hover:bg-red-700 text-white font-semibold px-6 py-2.5 rounded-md transition-colors" data-testid="hiw-claim-cta">Claim your property</a>
        </section>
      </div>
    </div>
  );
}
